<script>
import svgCache from '@/utils/svgCache'

// [PERF] AppIcon antes era un componente Vue normal con data() + 3 watchers + un
// loadSvg() async ejecutado en cada montaje. En listas grandes (p.ej. la tabla de
// transacciones bancarias con 500 filas × ~5 AppIcons por fila = 2500 instancias)
// eso supone 2500 instancias Vue completas, 7500 watchers y 2500 promises async
// que pasan por microtask queue antes de pintar. Cada instancia añadía decenas
// de ms al render flush.
//
// Ahora es un componente FUNCTIONAL: no crea instancia Vue, no tiene watchers,
// no tiene data() ni lifecycle. Lee el SVG del cache síncronamente. Si no está
// cacheado, dispara un load fire-and-forget y muestra un placeholder vacío hasta
// que el siguiente render lo encuentre cacheado.
//
// Caveat: la primera vez que aparece un (src,colorMap) nuevo, la primera fila no
// muestra el SVG hasta el siguiente tick. En la práctica los iconos del listado
// ya están en cache (se reutilizan), así que la mayoría de filas pintan
// inmediatamente y todas las restantes en el siguiente render.

export default {
  name: 'AppIcon',
  functional: true,
  props: {
    src: { type: String, required: true },
    size: { type: [Number, String], default: 24 },
    colorMap: { type: Object, default: null },
    preserveColors: { type: Boolean, default: false },
  },
  render(h, ctx) {
    // [PERF] Suscribir esta render function a la versión del cache (Vue.observable).
    // Cuando un SVG nuevo termine de cargar (vía prime), `_version` incrementa, y este
    // render se vuelve a ejecutar — momento en que el `getSync` SÍ encontrará el SVG
    // ya cacheado y pintará el icono. Sin esta suscripción, el icono se quedaba en
    // blanco hasta que algo más disparara un re-render (ej. hover sobre la fila).
    svgCache.getVersion()

    const { src, size, colorMap, preserveColors } = ctx.props
    const svg = svgCache.getSync(src, colorMap, preserveColors)

    // attrs comunes a ambas ramas. data-src se expone para que tests/devtools puedan
    // inspeccionar qué SVG se cargó sin tener que mirar el DOM serializado interno.
    const commonData = {
      class: 'app-icon',
      style: { width: size + 'px', height: size + 'px' },
      attrs: { 'data-src': src, 'data-size': String(size) },
      on: ctx.listeners,
    }

    if (svg == null) {
      // No cacheado todavía: lanzamos el load fire-and-forget y pintamos un hueco
      // del tamaño correcto para no provocar layout shifts.
      const vuetify = ctx.parent && ctx.parent.$vuetify
      svgCache.prime(src, colorMap, preserveColors, vuetify)
      return h('div', commonData)
    }

    return h('div', { ...commonData, domProps: { innerHTML: svg } })
  },
}
</script>

<style>
.app-icon {
  display: inline-block;
  vertical-align: middle;
  line-height: 0;
  color: inherit;
}

.app-icon svg {
  display: block;
  width: 100%;
  height: 100%;
  color: inherit;
}

/* Fallback color only when there is no color inherited */
.v-btn:not([class*="white"]) .app-icon,
.app-icon:not(.v-btn *) {
  color: var(--v-primary-base);
}
</style>
